/**
 * KOPS-329: Brokerage Pricing Tier Constants
 * New pricing structure for brokerage billing
 */

export interface BrokerageTier {
  name: string;
  minAgents: number;
  maxAgents: number;
  monthlyPrice: number;
  features: string[];
}

export interface BrokeragePricing {
  implementation: {
    oneTime: number;
    description: string;
    includes: string[];
  };
  agentFee: number;
  websiteFee: number;
  tiers: {
    standard: BrokerageTier;
    stability: BrokerageTier;
    small: BrokerageTier;
    medium: BrokerageTier;
    large: BrokerageTier;
  };
}

export const BROKER_PRICING_TIERS: BrokeragePricing = {
  implementation: {
    oneTime: 2000,
    description: "30-Day Implementation & Onboarding",
    includes: [
      "Dedicated onboarding specialist",
      "Kickoff strategy call",
      "Complete system setup",
      "Agent training sessions (up to 3 sessions)",
      "30-day support and troubleshooting",
      "Custom workflow configuration"
    ]
  },
  // KOPS-465 pricing update — compliance is FREE for all agents.
  // $70/mo Agent Hub is optional — only charged when the broker
  // chooses to cover CRM + MLS search for a specific agent.
  // The dashboard calculates fees by counting only agents on the
  // 'agent-hub' team_plan, not all agents.
  agentFee: 70, // Per AGENT HUB agent monthly fee (compliance-only agents are $0)
  websiteFee: 0, // No additional website fee - included in base
  tiers: {
    standard: {
      name: "Standard Office Plan",
      minAgents: 0,
      maxAgents: 999999,
      monthlyPrice: 500,
      features: [
        "Multiple offices? Just $500 each",
        "No per-transaction fees",
        "Unlimited AI compliance checks",
        "5-year document storage included",
        "AI Document Compliance Review",
        "MLS Integration",
        "Fair Housing Compliance Monitoring",
        "Agent Management Dashboard",
        "Commission Tracking & Reporting",
        "License & Certification Management",
        "Inter-office Communication Tools",
        "Broker Assistant AI"
      ]
    },
    // Legacy tiers kept for backward compatibility but hidden from UI
    stability: {
      name: "Legacy Stability Plan",
      minAgents: 0,
      maxAgents: 20,
      monthlyPrice: 500,
      features: []
    },
    small: {
      name: "Legacy Small Brokerage",
      minAgents: 21,
      maxAgents: 50,
      monthlyPrice: 500,
      features: []
    },
    medium: {
      name: "Legacy Medium Brokerage",
      minAgents: 51,
      maxAgents: 200,
      monthlyPrice: 500,
      features: []
    },
    large: {
      name: "Legacy Large Brokerage",
      minAgents: 201,
      maxAgents: 999999,
      monthlyPrice: 500,
      features: []
    }
  }
};

/**
 * Determine the appropriate pricing tier based on agent count
 * Note: Always returns 'standard' for flat pricing model
 */
export const getCurrentTier = (agentCount: number): 'standard' | 'stability' | 'small' | 'medium' | 'large' => {
  // New flat pricing model - always return 'standard'
  return 'standard';
};

/**
 * Calculate total monthly billing for a brokerage
 */
export interface BillingCalculation {
  tierName: string;
  tierPrice: number;
  agentCount: number;
  agentFees: number;
  perAgentFee: number;
  websiteFee: number;
  subtotal: number;
  total: number;
}

export const calculateMonthlyTotal = (
  agentCount: number,
  hasWebsite: boolean
): BillingCalculation => {
  const tier = getCurrentTier(agentCount);
  const tierInfo = BROKER_PRICING_TIERS.tiers[tier];
  const tierPrice = tierInfo.monthlyPrice;
  const agentFees = agentCount * BROKER_PRICING_TIERS.agentFee;
  const websiteFee = hasWebsite ? BROKER_PRICING_TIERS.websiteFee : 0;

  const subtotal = tierPrice + agentFees;
  const total = subtotal + websiteFee;

  return {
    tierName: tierInfo.name,
    tierPrice,
    agentCount,
    agentFees,
    perAgentFee: BROKER_PRICING_TIERS.agentFee,
    websiteFee,
    subtotal,
    total
  };
};

/**
 * Check if brokerage is approaching tier limit
 */
export const isApproachingTierLimit = (agentCount: number, tier: 'standard' | 'stability' | 'small' | 'medium' | 'large'): boolean => {
  const tierInfo = BROKER_PRICING_TIERS.tiers[tier];
  // Flat pricing model has no limits
  if (tier === 'standard') return false;
  return agentCount > tierInfo.maxAgents * 0.9; // 90% threshold
};

/**
 * Get next tier information
 */
export const getNextTier = (currentTier: 'standard' | 'stability' | 'small' | 'medium' | 'large'): BrokerageTier | null => {
  // Flat pricing model has no upgrade path
  if (currentTier === 'standard') return null;
  // Legacy tier upgrades
  if (currentTier === 'stability') return BROKER_PRICING_TIERS.tiers.small;
  if (currentTier === 'small') return BROKER_PRICING_TIERS.tiers.medium;
  if (currentTier === 'medium') return BROKER_PRICING_TIERS.tiers.large;
  return null; // Already at highest tier
};
